import { useCallback, useState } from "react";
import { createIsomorphicFn } from "@tanstack/react-start";
import { getCookie as getServerCookie } from "@tanstack/react-start/server";

import { getCookie, setCookie } from "@/utils/cookies";

/**
 * Whether the reader collapsed the sidebar to its icon rail (R-2295). A
 * browser choice rather than an account setting, so it lives in a cookie:
 * read on the server too, the first paint of a page already draws the rail
 * the reader left, instead of the full sidebar snapping shut after
 * hydration. First visit is expanded.
 */
export const SIDEBAR_COLLAPSED_COOKIE = "sidebarCollapsed";
const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

const readCollapsedCookie = createIsomorphicFn()
  .client(() => getCookie(SIDEBAR_COLLAPSED_COOKIE))
  .server(() => getServerCookie(SIDEBAR_COLLAPSED_COOKIE));

function writeCollapsedCookie(collapsed: boolean) {
  setCookie(SIDEBAR_COLLAPSED_COOKIE, String(collapsed), {
    maxAge: ONE_YEAR_SECONDS,
    sameSite: "lax",
  });
}

export default function useSidebarCollapsed() {
  const [collapsed, setCollapsedState] = useState(
    () => readCollapsedCookie() === "true"
  );

  const setCollapsed = useCallback((next: boolean) => {
    writeCollapsedCookie(next);
    setCollapsedState(next);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsedState((previous) => {
      const next = !previous;
      writeCollapsedCookie(next);
      return next;
    });
  }, []);

  return { collapsed, setCollapsed, toggleCollapsed };
}

/**
 * ⌘B on a Mac and Ctrl+B elsewhere switch between the rail and the full
 * sidebar, as the design names them. Both modifiers are accepted on every
 * platform, as editors do, so nothing has to sniff the OS.
 */
export function isSidebarShortcut(event: KeyboardEvent): boolean {
  return (
    (event.metaKey || event.ctrlKey) &&
    !event.altKey &&
    !event.shiftKey &&
    event.key.toLowerCase() === "b" &&
    !event.defaultPrevented
  );
}
